import * as React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Ширина окна — классом на ВНЕШНЕЙ обёртке. Задавать её на DialogContent
   *  бесполезно: ширину держит обёртка, и `sm:max-w-3xl` на контенте молча ни на
   *  что не влиял — диалог оставался в 32rem, а двухколоночная вёрстка внутри
   *  сжималась (Алексей 18.09, AI58: «UI поехал, всё слева сжалось»). */
  widthClassName?: string;
}

const DialogContext = React.createContext<{ onClose: () => void } | null>(null);

/** Lightweight modal — full-screen backdrop + centred panel.
 *  Closes on Escape and on backdrop click. */
export function Dialog({ open, onOpenChange, children, widthClassName }: DialogProps) {
  const onClose = React.useCallback(() => onOpenChange(false), [onOpenChange]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <DialogContext.Provider value={{ onClose }}>
      {/* data-overlay marks this portaled tree so outside-pointerdown
          listeners (e.g. ResizableSidebar's collapse-on-tap) skip taps that
          land inside the dialog/backdrop and don't tear it down. */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-overlay>
        <div
          className="absolute inset-0 bg-black/40 animate-in fade-in duration-150"
          onClick={onClose}
          aria-hidden
        />
        <div className={cn('relative z-10 w-full animate-in fade-in zoom-in-95 duration-150', widthClassName ?? 'max-w-lg')}>
          {children}
        </div>
      </div>
    </DialogContext.Provider>,
    document.body
  );
}

export function DialogContent({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const ctx = React.useContext(DialogContext);
  return (
    <div
      role="dialog"
      aria-modal="true"
      className={cn(
        'rounded-xl border border-border bg-card text-card-foreground shadow-xl',
        'p-6',
        className
      )}
      {...props}
    >
      {children}
      {ctx && (
        <button
          type="button"
          onClick={ctx.onClose}
          className="absolute right-3 top-3 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label="Закрыть"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
}

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-4 flex flex-col gap-1.5', className)} {...props} />;
}

export function DialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn('text-lg font-semibold leading-tight', className)} {...props} />;
}

export function DialogDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}
